import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Body1,
  Caption1,
  Spinner,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { useProducts } from '../hooks/useProducts'
import type { Product, ProductLifecycleStage } from '../types/product'
import { LIFECYCLE_LABELS } from './ProductList'
import { apiErrorMessage } from '../lib/apiError'
import { activatable } from '../lib/activatable'

/**
 * Product Kanban — a read-only board grouped by lifecycle stage. Gate
 * transitions happen via the gate-review panel on the detail page (not by
 * dragging), so this is a scannable overview: click a card to open it.
 */

const COLUMNS: { key: ProductLifecycleStage; color: string }[] = [
  { key: 'poc', color: '#0891b2' },
  { key: 'mvp', color: '#8b5cf6' },
  { key: 'portfolio', color: '#16a34a' },
  { key: 'sunset', color: '#9ca3af' },
]

const useStyles = makeStyles({
  root: { maxWidth: '1200px', margin: '0 auto' },
  board: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-start',
    overflowX: 'auto',
    paddingBottom: tokens.spacingVerticalM,
  },
  column: {
    flex: '1 0 260px',
    minWidth: '260px',
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: tokens.spacingHorizontalS,
  },
  colHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px 10px',
    fontWeight: 600,
  },
  dot: { width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block' },
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '10px 12px',
    marginBottom: '8px',
    cursor: 'pointer',
    ':hover': { border: `1px solid ${tokens.colorNeutralStroke1}`, background: '#fafafa' },
  },
  cardTitle: { fontWeight: 600, fontSize: '14px' },
  cardMeta: { color: tokens.colorNeutralForeground3, fontSize: '12px', marginTop: '2px' },
  cardBadges: { display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' },
  empty: { color: tokens.colorNeutralForeground3, fontSize: '12px', padding: '8px' },
  state: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'center',
    padding: '48px 16px',
  },
})

export function ProductKanban() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { data, isLoading, error } = useProducts({ page_size: 500 })

  const byStage = useMemo(() => {
    const map: Record<string, Product[]> = { poc: [], mvp: [], portfolio: [], sunset: [] }
    for (const p of data?.items ?? []) {
      const bucket = map[p.lifecycle_stage]
      if (bucket) bucket.push(p)
    }
    return map
  }, [data])

  return (
    <section className={styles.root} aria-label="Product kanban">
      <Title2 as="h1" style={{ color: '#242424', marginBottom: tokens.spacingVerticalL }}>
        Product pipeline
      </Title2>

      {isLoading ? (
        <div className={styles.state} data-testid="product-kanban-loading">
          <Spinner size="tiny" />
          <Body1>Loading…</Body1>
        </div>
      ) : error ? (
        <div className={styles.state} role="alert" data-testid="product-kanban-error">
          <Body1>Failed to load products. {apiErrorMessage(error)}</Body1>
        </div>
      ) : (
        <div className={styles.board} data-testid="product-kanban-board">
          {COLUMNS.map((col) => {
            const items = byStage[col.key] ?? []
            return (
              <div key={col.key} className={styles.column} data-testid={`product-col-${col.key}`}>
                <div className={styles.colHead}>
                  <span className={styles.dot} style={{ background: col.color }} />
                  {LIFECYCLE_LABELS[col.key]}
                  <Badge appearance="tint" size="small" color="informative">
                    {items.length}
                  </Badge>
                </div>
                {items.length === 0 ? (
                  <div className={styles.empty}>No products</div>
                ) : (
                  items.map((p) => (
                    <div
                      key={p.id}
                      className={styles.card}
                      data-testid={`product-card-${p.id}`}
                      role="button"
                      {...activatable(() => navigate(`/product/${p.id}`))}
                    >
                      <div className={styles.cardTitle}>{p.name}</div>
                      <div className={styles.cardMeta}>
                        {p.public_id ?? `#${p.id}`}
                      </div>
                      <div className={styles.cardBadges}>
                        <Badge appearance="tint" color="brand" size="small">
                          {p.focus_area_code}
                        </Badge>
                        {p.gate_status && (
                          <Badge
                            appearance="tint"
                            size="small"
                            color={p.gate_status === 'overdue' ? 'danger' : 'warning'}
                          >
                            {p.gate_status}
                          </Badge>
                        )}
                        {p.team.length > 0 && (
                          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                            {p.team.length} on team
                          </Caption1>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default ProductKanban
